"use client"

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuthToken } from "@/hooks/use-auth"

// API client configuration - empty string means same origin (Vite proxies /api to Express)
const API_BASE = ""

// ============================================
// Type Definitions
// ============================================

export interface Permission {
  id: string
  name: string
  description?: string | null
  resource: string
  action: string
}

export interface Role {
  id: string
  name: string
  description: string | null
  userCount: number
  permissions: Permission[]
  createdAt: string
}

export interface CreateRoleInput {
  name: string
  description?: string
  permissionIds?: string[]
}

export interface UpdateRoleInput {
  name?: string
  description?: string | null
  permissionIds?: string[]
}

// ============================================
// API Response Types
// ============================================

interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  meta: {
    pagination: {
      total: number
      page: number
      pageSize: number
      totalPages: number
    }
  }
}

interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
}

// ============================================
// Fetch helpers
// ============================================

async function fetchRoles(
  token: string | null,
  params?: { page?: number; pageSize?: number; search?: string }
): Promise<{ roles: Role[]; total: number }> {
  if (!token) return { roles: [], total: 0 }

  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.set("page", params.page.toString())
  if (params?.pageSize) searchParams.set("pageSize", params.pageSize.toString())
  if (params?.search) searchParams.set("search", params.search)

  const queryString = searchParams.toString()
  const response = await fetch(
    `${API_BASE}/api/roles${queryString ? `?${queryString}` : ""}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  )

  if (!response.ok) {
    if (response.status === 401) return { roles: [], total: 0 }
    throw new Error("Failed to fetch roles")
  }

  const data = await response.json()
  
  // Handle both paginated and non-paginated responses
  if (data.meta?.pagination) {
    return {
      roles: data.data,
      total: data.meta.pagination.total,
    }
  }
  
  // Non-paginated response (data.data is the array of roles)
  const roles = Array.isArray(data.data) ? data.data : []
  return {
    roles,
    total: roles.length,
  }
}

async function fetchRole(roleId: string, token: string | null): Promise<Role | null> {
  if (!token) return null

  const response = await fetch(`${API_BASE}/api/roles/${roleId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    if (response.status === 404) return null
    throw new Error("Failed to fetch role")
  }

  const data: ApiResponse<Role> = await response.json()
  return data.data
}

async function fetchPermissions(token: string | null): Promise<{
  permissions: Permission[]
  byResource: Record<string, Permission[]>
}> {
  if (!token) return { permissions: [], byResource: {} }

  const response = await fetch(`${API_BASE}/api/permissions`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    if (response.status === 401) return { permissions: [], byResource: {} }
    throw new Error("Failed to fetch permissions")
  }

  const data: ApiResponse<{ permissions: Permission[]; byResource: Record<string, Permission[]> }> =
    await response.json()
  return data.data
}

async function createRole(input: CreateRoleInput, token: string | null): Promise<Role> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/roles`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to create role")
  }

  const data: ApiResponse<Role> = await response.json()
  return data.data
}

async function updateRole(
  roleId: string,
  input: UpdateRoleInput,
  token: string | null
): Promise<Role> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/roles/${roleId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to update role")
  }

  const data: ApiResponse<Role> = await response.json()
  return data.data
}

async function deleteRole(roleId: string, token: string | null): Promise<void> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/roles/${roleId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to delete role")
  }
}

// ============================================
// Hooks
// ============================================

export function useRoles(params?: { page?: number; pageSize?: number; search?: string }) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["roles", params],
    queryFn: () => fetchRoles(token, params),
    enabled: !!token,
  })
}

export function useRole(roleId: string) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["roles", roleId],
    queryFn: () => fetchRole(roleId, token),
    enabled: !!roleId && !!token,
  })
}

export function usePermissions() {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["permissions"],
    queryFn: () => fetchPermissions(token),
    enabled: !!token,
  })
}

export function useCreateRole() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (input: CreateRoleInput) => createRole(input, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] })
    },
  })
}

export function useUpdateRole() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: ({ roleId, ...input }: UpdateRoleInput & { roleId: string }) =>
      updateRole(roleId, input, token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["roles"] })
      queryClient.invalidateQueries({ queryKey: ["roles", variables.roleId] })
    },
  })
}

export function useDeleteRole() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (roleId: string) => deleteRole(roleId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] })
    },
  })
}

